import { IOrderSearchResult, convertToEuros, getOrderDisplayAddress } from "@app/common";
import { memo } from "react";
import styled from "@2pulse/responsive";
import { normalize } from "@app/helpers";
import { theme } from "@app/theme";
import { DateTime } from "luxon";

interface OrderSearchResultCardProps {
  result: IOrderSearchResult;
}

export const OrderSearchResultCard = memo(({ result }: OrderSearchResultCardProps) => {
  const customerName = [result.customer?.firstname, result.customer?.lastname].filter(Boolean).join(' ');
  const address = getOrderDisplayAddress(result);

  return (
    <ResultContainer>
      <ResultHeader>
        <ResultShopBadge>{result.shop.publicName || result.shop.internalName}</ResultShopBadge>
        <ResultData>#{result.serviceId}</ResultData>
        <ResultData>{DateTime.fromISO(result.createdAt).toFormat('dd/MM/yyyy HH:mm')}</ResultData>
        <ResultData>{convertToEuros(result.total)}€</ResultData>
        {!!customerName && <ResultData>{customerName}</ResultData>}
        {!!result.customer?.phone && <ResultData>{result.customer.phone}</ResultData>}
        {!!result.platformCode && <ResultData>#{result.platformCode}</ResultData>}
      </ResultHeader>

      {!!address && <ResultLine>Adresse : {address}</ResultLine>}

      {(result.items ?? []).map((item, idx) => (
        <ResultLine key={`item-${idx}`}>- {item.variationName ?? item.name} x{item.quantity ?? 1}</ResultLine>
      ))}

      <ServiceLink href={`/services?_id=${result.service._id}`}>
        Voir le service ({DateTime.fromISO(result.service.fromDate).toFormat('dd/MM')} → {DateTime.fromISO(result.service.toDate).toFormat('dd/MM')})
      </ServiceLink>
    </ResultContainer>
  );
});

// L'écran de recherche vit sur le fond clair de MainLayout : la carte reprend le
// panneau sombre maison (cf. SimpleList) pour que les textes babypowder restent lisibles.
const ResultContainer = styled.View({
  width: '100%',
  backgroundColor: '#0B2032',
  borderRadius: normalize(21),
  padding: normalize(21),
  marginBottom: normalize(13),
});

const ResultHeader = styled.View({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: normalize(10),
  rowGap: normalize(6),
  marginBottom: normalize(8),
});

const ResultShopBadge = styled.Text({
  backgroundColor: theme.colors.celadonblue,
  color: theme.colors.babypowder,
  fontWeight: 'bold',
  fontSize: normalize(14),
  borderRadius: normalize(10),
  paddingHorizontal: normalize(10),
  paddingVertical: normalize(4),
});

const ResultData = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(16),
  fontWeight: 'bold',
});

const ResultLine = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(14),
  paddingVertical: normalize(2),
});

const ServiceLink = ({ href, children }: { href: string, children: React.ReactNode }) => (
  <a style={{
    color: theme.colors.celadonblue,
    textDecoration: 'underline',
    textDecorationStyle: 'dashed',
    textUnderlineOffset: 4,
    marginTop: normalize(8),
    display: 'inline-block',
  }} href={href}>{children}</a>
);
